import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('loading'); // loading, success, failed
    const [paymentInfo, setPaymentInfo] = useState(null);
    const { clearCart } = useCart();
    const sessionId = searchParams.get('session_id');

    useEffect(() => {
        const checkPaymentStatus = async () => {
            if (!sessionId) {
                setStatus('failed');
                return;
            }

            let attempts = 0;
            const maxAttempts = 5;
            const pollInterval = 2000;

            const poll = async () => {
                try {
                    const response = await axios.get(`${API}/checkout/status/${sessionId}`);
                    const data = response.data;

                    if (data.payment_status === 'paid') {
                        setStatus('success');
                        setPaymentInfo(data);
                        clearCart();
                        return;
                    } else if (data.status === 'expired') {
                        setStatus('failed');
                        return;
                    }

                    // Continue polling if still pending
                    attempts++;
                    if (attempts < maxAttempts) {
                        setTimeout(poll, pollInterval);
                    } else {
                        // After max attempts, assume success if we got here from Stripe
                        setStatus('success');
                        clearCart();
                    }
                } catch (error) {
                    console.error('Error checking payment status:', error);
                    attempts++;
                    if (attempts < maxAttempts) {
                        setTimeout(poll, pollInterval);
                    } else {
                        setStatus('failed');
                    }
                }
            };

            poll();
        };

        checkPaymentStatus();
    }, [sessionId, clearCart]);

    return (
        <div data-testid="checkout-success-page" className="min-h-screen bg-brand-bg pt-24 flex items-center justify-center">
            <div className="max-w-md mx-auto px-6 text-center">
                {status === 'loading' && (
                    <div data-testid="payment-loading">
                        <Loader2 className="w-16 h-16 text-brand-primary mx-auto mb-6 animate-spin" />
                        <h1 className="font-syne font-bold text-2xl text-white mb-4">
                            PROCESSING PAYMENT...
                        </h1>
                        <p className="font-space text-slate-400">
                            Please wait while we confirm your order.
                        </p>
                    </div>
                )}

                {status === 'success' && (
                    <div data-testid="payment-success">
                        <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
                        <h1 className="font-syne font-extrabold text-4xl text-white mb-4 uppercase tracking-tight">
                            ORDER CONFIRMED
                        </h1>
                        <p className="font-space text-slate-400 mb-8">
                            Thank you for your purchase! You'll receive a confirmation email shortly.
                        </p>
                        {paymentInfo && (
                            <p className="font-mono text-sm text-slate-500 mb-8">
                                Total: ${(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                            </p>
                        )}
                        <div className="space-y-4">
                            <Link to="/merch">
                                <Button 
                                    data-testid="continue-shopping-btn"
                                    className="w-full bg-white text-black hover:bg-slate-200 rounded-none py-4 font-bold uppercase tracking-widest"
                                >
                                    CONTINUE SHOPPING <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </Link>
                            <Link to="/">
                                <Button 
                                    variant="outline"
                                    className="w-full bg-transparent border border-white/20 text-white hover:bg-white/10 rounded-none py-4 uppercase tracking-widest"
                                >
                                    BACK TO HOME
                                </Button>
                            </Link>
                        </div>
                    </div>
                )}

                {status === 'failed' && (
                    <div data-testid="payment-failed">
                        <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
                        <h1 className="font-syne font-extrabold text-4xl text-white mb-4 uppercase tracking-tight">
                            PAYMENT FAILED
                        </h1>
                        <p className="font-space text-slate-400 mb-8">
                            Something went wrong with your payment. Please try again.
                        </p>
                        <div className="space-y-4">
                            <Link to="/checkout">
                                <Button 
                                    data-testid="retry-payment-btn"
                                    className="w-full bg-white text-black hover:bg-slate-200 rounded-none py-4 font-bold uppercase tracking-widest"
                                >
                                    TRY AGAIN
                                </Button>
                            </Link>
                            <Link to="/merch">
                                <Button 
                                    variant="outline"
                                    className="w-full bg-transparent border border-white/20 text-white hover:bg-white/10 rounded-none py-4 uppercase tracking-widest"
                                >
                                    BACK TO SHOP
                                </Button>
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
